import { Fragment, type ReactNode, useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  Outlet,
  type RouteObject,
  RouterProvider as ReactRouterProvider,
  useLocation,
} from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";

import { PostHogProvider } from "@posthog/react";
import { useFeatureFlagOverridesKey } from "@vega-api/extended/auth/featureFlagOverrides";
import { useUiCapabilityOverridesKey } from "@vega-api/extended/auth/uiCapabilityOverrides";
import { type FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { TooltipRoot } from "@vega-core/components/Deprecated/TooltipRoot/TooltipRoot";
import { StatusPopupProvider } from "@vega-core/components/StatusPopup/StatusPopupProvider";
import { RouterProvider } from "@vega-core/context/RouterContext/RouterProvider";
import { ThemeProvider } from "@vega-core/context/ThemeContext/ThemeProvider";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { TrackRouteHistory } from "@vega-core/stores/navigationHistory.store";
import type { ThemesList } from "@vega-core/themeConfig/themesModel";
import { initPostHog } from "@vega-core/utils/posthog/initPostHog";

import { DevCommandPalette } from "../../components/DevCommandPalette/DevCommandPalette";
import { MaintenanceWrapper } from "../../components/MaintenanceWrapper/MaintenanceWrapper";
import { TasksStateProvider } from "../../context/TasksStateContext/context";

import { createPortalRouter } from "./createPortalRouter";
import { TrackFaroView } from "./TrackFaroView";

import "inter-ui/inter.css";

type ProvidersWrapperProps = {
  children?: ReactNode;
  outletWrapper?: (outlet: ReactNode) => ReactNode;
  portal?: FrontendPortal;
  routes?: RouteObject[];
  themeOverride?: ThemesList;
  routeConfig?: {
    path: string;
    initialEntry: string;
  };
};

const QueryProvider = ({ children }: { children: ReactNode }) => (
  <QueryClientProvider client={QueryClientInstance}>
    {children}
  </QueryClientProvider>
);

initPostHog();

export const ProvidersWrapper = ({
  children,
  outletWrapper,
  portal,
  routes,
  themeOverride,
  routeConfig,
}: ProvidersWrapperProps) => {
  const [router] = useState(() => {
    const outlet = <Outlet />;
    const content = routes ? (outletWrapper?.(outlet) ?? outlet) : children;
    const routerContent = (
      <>
        <ApplyThemeTabTitle />
        <TrackRouteHistory />
        <TrackFaroView />
        <TasksStateProvider portal={portal}>
          <RouterProvider>
            <>
              <div id="sidebar-modal-root" />
              <DevCommandPalette />
              <MaintenanceWrapper>
                <FeatureFlagRemountBoundary>
                  <div
                    className="flex flex-col min-h-dvh max-w-[100vw]"
                    data-testid="app"
                  >
                    {content}
                  </div>
                </FeatureFlagRemountBoundary>
              </MaintenanceWrapper>
            </>
          </RouterProvider>
        </TasksStateProvider>
      </>
    );

    const portalRoutes = routes
      ? [{ children: routes, element: routerContent }]
      : [{ element: routerContent, path: routeConfig?.path ?? "*" }];

    return createPortalRouter({
      routeConfig,
      routes: portalRoutes,
      skipFaroInstrumentation: !routes,
    });
  });

  return (
    <PostHogProvider client={posthog}>
      <HelmetProvider>
        <QueryProvider>
          <SessionProvider>
            <ThemeProvider themeOverride={themeOverride}>
              <StatusPopupProvider>
                <TooltipRoot />
                <ReactRouterProvider router={router} />
              </StatusPopupProvider>
            </ThemeProvider>
          </SessionProvider>
        </QueryProvider>
      </HelmetProvider>
    </PostHogProvider>
  );
};

/**
 * Forces a remount of the app subtree whenever feature flag or ui-capability
 * overrides change. Re-renders alone aren't enough — route guards, mount-time
 * redirects, and initial-state hooks evaluate flags on mount and need a fresh
 * tree to pick up an override. Outside local/test the stores are permanently
 * empty so the key is a stable constant and no remount can fire.
 */
function FeatureFlagRemountBoundary({ children }: { children: ReactNode }) {
  const featureFlagOverridesKey = useFeatureFlagOverridesKey();
  const capabilityOverridesKey = useUiCapabilityOverridesKey();

  return (
    <Fragment key={`${featureFlagOverridesKey}|${capabilityOverridesKey}`}>
      {children}
    </Fragment>
  );
}

export function ApplyThemeTabTitle() {
  const { theme } = usePublicConfig();
  const { pathname } = useLocation();

  useEffect(() => {
    // Sets the document title to the theme's configured tab title
    // Note: This does not change based on routes - it applies the same title
    // configured in the theme regardless of navigation
    // The title persists when navigating outside of Sanity routes, but gets
    // updated within the Sanity black-box when those routes are active
    document.title = theme.tabTitle;

    const existingFaviconLinks =
      document.querySelectorAll<HTMLLinkElement>("link[rel='icon']");
    existingFaviconLinks.forEach((link) => {
      if (link.getAttribute("sizes")) {
        link.remove(); // Remove PNG favicons with sizes attribute
      }
    });

    let mainFaviconLink = document.querySelector<HTMLLinkElement>(
      "link[rel='icon']:not([sizes])",
    );

    if (!mainFaviconLink) {
      mainFaviconLink = document.createElement("link");
      mainFaviconLink.rel = "icon";
      document.head.appendChild(mainFaviconLink);
    }

    mainFaviconLink.href = theme.favicon;
  }, [pathname, theme]);

  return null;
}

import { QueryClientInstance } from "@vega-api/configs/QueryClient";
import { SessionProvider } from "@vega-api/custom/SessionContext/SessionProvider";
